import deDE from '@shared/locales/de-DE.json';
import enAU from '@shared/locales/en-AU.json';
import enCA from '@shared/locales/en-CA.json';
import enGB from '@shared/locales/en-GB.json';
import enUS from '@shared/locales/en-US.json';
import es001 from '@shared/locales/es-001.json';
import esES from '@shared/locales/es-ES.json';
import esMX from '@shared/locales/es-MX.json';
import frFR from '@shared/locales/fr-FR.json';
import hiIN from '@shared/locales/hi-IN.json';
import itIT from '@shared/locales/it-IT.json';
import jaJP from '@shared/locales/ja-JP.json';
import koKR from '@shared/locales/ko-KR.json';
import nlNL from '@shared/locales/nl-NL.json';
import plPL from '@shared/locales/pl-PL.json';
import ptBR from '@shared/locales/pt-BR.json';
import roRO from '@shared/locales/ro-RO.json';
import srYU from '@shared/locales/sr-YU.json';
import svSE from '@shared/locales/sv-SE.json';
import trTR from '@shared/locales/tr-TR.json';
import viVN from '@shared/locales/vi-VN.json';
import zhCN from '@shared/locales/zh-CN.json';
import zhTW from '@shared/locales/zh-TW.json';
import { type App, nextTick, type Plugin, watchEffect } from 'vue';

type I18nInstance = {
  global: {
    locale: { value: string };
    mergeLocaleMessage(locale: string, messages: Record<string, unknown>): void;
    getLocaleMessage(locale: string): Record<string, unknown>;
    availableLocales: string[];
  };
};

const sharedMessages = {
  'de-DE': deDE,
  'en-AU': enAU,
  'en-CA': enCA,
  'en-GB': enGB,
  'en-US': enUS,
  'es-001': es001,
  'es-ES': esES,
  'es-MX': esMX,
  'fr-FR': frFR,
  'hi-IN': hiIN,
  'it-IT': itIT,
  'ja-JP': jaJP,
  'ko-KR': koKR,
  'nl-NL': nlNL,
  'pl-PL': plPL,
  'pt-BR': ptBR,
  'ro-RO': roRO,
  'sr-YU': srYU,
  'sv-SE': svSE,
  'tr-TR': trTR,
  'vi-VN': viVN,
  'zh-CN': zhCN,
  'zh-TW': zhTW,
} as const;

type SharedLocale = keyof typeof sharedMessages;

const isSharedLocale = (locale: string): locale is SharedLocale => {
  return locale in sharedMessages;
};

export const SharedPlugin: Plugin = {
  install(_app: App, i18n: I18nInstance) {
    const unwatch = watchEffect(() => {
      const currentLocale = i18n.global.locale.value;

      if (isSharedLocale(currentLocale)) {
        const existingMessages = i18n.global.getLocaleMessage(currentLocale);

        if (!('ui' in existingMessages)) {
          i18n.global.mergeLocaleMessage(currentLocale, sharedMessages[currentLocale]);
        }

        if (Object.keys(sharedMessages).every((locale) => i18n.global.availableLocales.includes(locale))) {
          nextTick(() => {
            unwatch();
          });
        }
      }
    });
  },
};
